import React from 'react';
import { useEditor } from '@craftjs/core';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Type, Heading, Image, Video, Minus, DollarSign, Square, Columns,
  Undo, Redo, Trash2, Copy, Layers
} from 'lucide-react';
import {
  TextBlock,
  HeadingBlock,
  ImageBlock,
  VideoBlock,
  DividerBlock,
  PricingBlock,
  ContainerBlock,
  ColumnsBlock
} from './blocks';

const blocks = [
  { name: 'Heading', icon: Heading, component: HeadingBlock },
  { name: 'Text', icon: Type, component: TextBlock },
  { name: 'Image', icon: Image, component: ImageBlock },
  { name: 'Video', icon: Video, component: VideoBlock },
  { name: 'Divider', icon: Minus, component: DividerBlock },
  { name: 'Pricing', icon: DollarSign, component: PricingBlock },
  { name: 'Columns', icon: Columns, component: ColumnsBlock },
  { name: 'Section', icon: Square, component: ContainerBlock },
];

export const Toolbar = () => {
  const { isDark } = useTheme();
  const { connectors, actions, query, canUndo, canRedo } = useEditor((state, query) => ({
    canUndo: query.history.canUndo(),
    canRedo: query.history.canRedo()
  }));

  return (
    <div className={`p-4 border-b ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-gray-200 bg-gray-50'}`}>
      {/* Block Palette */}
      <div className="mb-4">
        <h3 className={`text-xs font-semibold uppercase mb-2 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
          Add Blocks
        </h3>
        <div className="flex flex-wrap gap-2">
          {blocks.map((block) => (
            <button
              key={block.name}
              ref={(ref) => ref && connectors.create(ref, <block.component />)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-grab active:cursor-grabbing ${
                isDark 
                  ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 hover:border-zinc-600' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              <block.icon className="h-4 w-4" />
              {block.name}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => actions.history.undo()}
          disabled={!canUndo}
          className={`p-2 rounded-lg border ${
            canUndo
              ? isDark ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : 'bg-white border-gray-300 hover:bg-gray-50'
              : isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-600' : 'bg-gray-100 border-gray-200 text-gray-400'
          }`}
          title="Undo"
        >
          <Undo className="h-4 w-4" />
        </button>
        <button
          onClick={() => actions.history.redo()}
          disabled={!canRedo}
          className={`p-2 rounded-lg border ${
            canRedo
              ? isDark ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : 'bg-white border-gray-300 hover:bg-gray-50'
              : isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-600' : 'bg-gray-100 border-gray-200 text-gray-400'
          }`}
          title="Redo"
        >
          <Redo className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};